import Layout, { Content } from 'antd/lib/layout/layout';
import Space from 'antd/lib/space';
import * as React from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { getAutomationIdByType, innerStepStatusColorForReleaseStepStatus } from '../helpers';
import { RootState } from '../reducers/reducers';
import { AutomationType, ReleaseStepCIDistribution, ReleaseStepCIDistributionData, ReleaseStepStatus } from '../types';
import styles from './styles/stepStyles';
import Text from 'antd/lib/typography/Text';
import WorkflowBuildModule from './workflowModule/WorkflowBuildModule';
import AutomatedTakeoffTasks from './AutomatedTakeoffTasks';
import { selectRelease, selectAppPlatform, selectReleaseStep, selectStepRun } from '../selectors/selectors';
import { ReleaseStepOrStepRunComponentProps } from '@/components/ValidatedRouteComponents';

const titleForStatus = (releaseStepStatus: ReleaseStepStatus) => {
    switch (releaseStepStatus) {
        case ReleaseStepStatus.Blocked:
        case ReleaseStepStatus.Inactive:
            return 'Not ready to release';
        case ReleaseStepStatus.Pending:
            return 'Ready to release';
        case ReleaseStepStatus.PendingChecklistOnly:
        case ReleaseStepStatus.Ready:
            return 'Released';
    }
};

const descriptionForStatus = (isReleased: boolean, stepStatus: ReleaseStepStatus) => {
    switch (stepStatus) {
        case ReleaseStepStatus.Blocked:
            return null;
        case ReleaseStepStatus.Inactive:
            return "You'll release your update over-the-air using the release workflow.";
        case ReleaseStepStatus.Ready:
        case ReleaseStepStatus.PendingChecklistOnly:
        case ReleaseStepStatus.Pending:
            return isReleased ? 'Your update has been released.' : 'Your update is ready to be released to users.';
    }
};

class StepContentCIDistribution extends React.Component<Props> {
    render() {
        return (
            <Layout>
                <Content
                    style={{
                        ...styles.innerContent,
                    }}
                >
                    <div style={{ display: 'flex', marginBottom: 30 }}>
                        <Space direction="vertical" size={4}>
                            <Text
                                style={{
                                    fontSize: 30,
                                    color: innerStepStatusColorForReleaseStepStatus(this.props.stepStatus),
                                    fontWeight: 600,
                                }}
                            >
                                {titleForStatus(this.props.stepStatus)}
                            </Text>
                            <Text type="secondary">
                                {descriptionForStatus(this.props.isReleased, this.props.stepStatus)}
                            </Text>
                        </Space>
                    </div>
                    {this.props.prodWorkflowData && (
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'stretch',
                                flexShrink: 0,
                                width: '100%',
                                marginBottom: 24,
                            }}
                        >
                            <WorkflowBuildModule
                                orgId={this.props.orgId}
                                appId={this.props.appId}
                                releaseId={this.props.releaseId}
                                releaseStepStatus={this.props.stepStatus}
                                releaseStepType={this.props.releaseStepType}
                                sdkVersion={this.props.sdkVersion}
                                platform={this.props.platform}
                                workflowData={this.props.prodWorkflowData}
                                buildInfo={this.props.prodBuild}
                                autoDownloadArtifactsAutomationId={this.props.autoDownloadArtifactsAutomationId}
                                autoUploadBinaryArtifactToAppStoreAutomationId={
                                    this.props.autoUploadBinaryArtifactToAppStoreAutomationId
                                }
                                autoUploadDsymsToStabilityMonitoringAutomationId={
                                    this.props.autoUploadDsymsToStabilityMonitoringAutomationId
                                }
                                stepRunComponentProps={this.props.stepRunComponentProps}
                            />
                        </div>
                    )}
                    <AutomatedTakeoffTasks
                        stepRunComponentProps={this.props.stepRunComponentProps}
                        orgId={this.props.orgId}
                        predictedTagName={this.props.expectedReleaseTagName}
                        releaseTagState={this.props.releaseTagState}
                        releaseTag={this.props.releaseTag}
                        releaseVersion={this.props.releaseVersion}
                        appId={this.props.appId}
                        releaseId={this.props.releaseId}
                        userId={this.props.userId}
                        codeDiffUrl={this.props.codeDiffUrl}
                    />
                </Content>
            </Layout>
        );
    }
}

const mapStateToProps = (state: RootState, ownProps: ReleaseStepOrStepRunComponentProps) => {
    const { orgId, appId, releaseId, releaseStepType } = ownProps;

    const userId = state.user.id;
    const release = selectRelease(state, releaseId);
    const releaseStep = selectReleaseStep(state, releaseId, releaseStepType) as ReleaseStepCIDistribution;
    const stepRunId = ownProps.stepRunComponentProps?.stepRunId;
    const stepRun = selectStepRun(state, stepRunId);

    const stepData: ReleaseStepCIDistributionData =
        stepRun ? { ...stepRun.data.summaryData, ...stepRun.data.complementaryData } : releaseStep.data;
    const platform = stepRun ? stepData.platform : selectAppPlatform(state, appId);

    const prodWorkflowData = stepData.workflowData;
    const prodBuild = stepData.prodBuild;
    const stepStatus = stepRun ? stepRun.status : releaseStep.status;
    const isReleased = stepData.isReleased;
    const sdkVersion = stepData.sdkVersion;
    const releaseVersion = release.version;

    const tagData = stepData.tagData;
    const expectedReleaseTagName = tagData.expectedReleaseTagName;
    const releaseTag = tagData.releaseTag;
    const releaseTagState = tagData.releaseTagState;
    const codeDiffUrl = tagData.codeDiffUrl;

    const automationRuns = stepRun ? stepRun.automationRuns : releaseStep.automationRuns;
    const autoUploadDsymsToStabilityMonitoringAutomationId = getAutomationIdByType(
        automationRuns,
        AutomationType.AutoUploadDsymsToStabilityMonitoring,
    );

    const autoUploadBinaryArtifactToAppStoreAutomationId = getAutomationIdByType(
        automationRuns,
        AutomationType.AutoUploadBinaryArtifactToAppStore,
    );

    const autoDownloadArtifactsAutomationId = getAutomationIdByType(
        automationRuns,
        AutomationType.AutoDownloadArtifacts,
    );

    const stepRunComponentProps = ownProps.stepRunComponentProps;

    return {
        orgId,
        appId,
        releaseId,
        platform,
        releaseStepType,
        autoDownloadArtifactsAutomationId,
        autoUploadBinaryArtifactToAppStoreAutomationId,
        autoUploadDsymsToStabilityMonitoringAutomationId,
        isReleased,
        stepStatus,
        releaseTagState,
        releaseTag,
        expectedReleaseTagName,
        userId,
        codeDiffUrl,
        prodWorkflowData,
        prodBuild,
        sdkVersion,
        releaseVersion,
        stepRunComponentProps,
    };
};

const connector = connect(mapStateToProps);

type Props = ConnectedProps<typeof connector>;

export default connector(StepContentCIDistribution);
